import type { SuperHubRouteParams } from '../types';
import type { ReadCustomBlockGitCollectionType } from '@readme/api/src/mappings/customblock/types';
import type { ReadGuideType } from '@readme/api/src/mappings/page/guide/types';
import type { GitSidebarCategory } from '@readme/api/src/mappings/sidebar/types';
import type { HubResponseProps } from '@readme/iso';

import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';

import type { ProjectContextValue } from '@core/context';
import { ProjectContext } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import useRdmdOpts from '@core/hooks/useRdmdOpts';
import {
  ConnectSuperHubDocumentToApi,
  ConnectSuperHubSidebarToApi,
  InitializeSuperHubDocument,
  useProjectStore,
  useSuperHubStore,
} from '@core/store';
import { useScrollTarget } from '@core/utils/ScrollTarget';
import ScrollTop from '@core/utils/ScrollTop';

import DocBody from '@routes/Doc/Body';
import Header from '@routes/Doc/Header';
import '@routes/Doc/style.scss';

import Footer from '@ui/DocFooter/SuperhubDocFooter';
import Sidebar from '@ui/Sidebar';
import Spinner from '@ui/Spinner';

import SectionGate from '../components/PlanAccess/SectionGate';
import ConnectRoute from '../ConnectRoute';
import RedirectToEmptyParentChild from '../RedirectToEmptyParentChild';
import RedirectToSidebarFirstPage from '../RedirectToSidebarFirstPage';

import EmptyGuide from './Empty';
import styles from './index.module.scss';

/**
 * Shape of route specific data for guides pages.
 */
export interface DocRouteProps {
  apiDefinitions?: never;
  customBlocks: ReadCustomBlockGitCollectionType['data'];
  document: ReadGuideType['data'];
  sidebar: GitSidebarCategory[];
}

function Content({ document: initialDocument, sidebar: initialSidebar, ...props }: HubResponseProps<DocRouteProps>) {
  const bem = useClassy(styles, 'SuperHubDoc');
  const { project } = useContext(ProjectContext) as ProjectContextValue;

  const [projectSubdomain, hideTOC, isFullWidth, enableSuggestedEdits, showAIDropdown] = useProjectStore(s => [
    s.data.subdomain,
    s.data.appearance.table_of_contents === 'disabled',
    s.data.appearance.layout.full_width === 'enabled',
    s.data.suggested_edits === 'enabled',
    s.data.appearance.ai?.dropdown === 'enabled',
  ]);

  const [isLoadingDocument, isLoadingSidebar, document, sidebar, routeSection, isAdmin] = useSuperHubStore(s => {
    return [
      // Only consider the document + sidebar to be "loading" if the initial SSR
      // data is different than what's currently in our store. This prevents an
      // unnecessary FOUC or unnecessary loading spinners on initial page load.
      s.document.isLoading && s.document.data !== initialDocument,
      s.sidebar.isLoading && s.sidebar.data !== initialSidebar,
      s.document.getGuidesPageData(),
      s.sidebar.data,
      s.routeSection,
      s.user.role === 'admin',
    ];
  });

  const rdmdOpts = useRdmdOpts(document ? { isMdx: document.renderable.status } : undefined);

  const isLoading = isLoadingDocument || isLoadingSidebar;

  // If we are on a loaded docs route with no slug and no document, that means
  // the `RedirectToSidebarFirstPage` HOC found an empty sidebar with no slug to
  // redirect to. Display the empty state in that case.
  const { slug } = useParams<SuperHubRouteParams>();
  if (!isLoading && !document && !slug) {
    return <EmptyGuide />;
  }

  // If the document is marked as not renderable, we can assume there is an MDX syntax error.
  const hasMdxError = !document?.renderable?.status;

  return (
    <main className={bem('&', 'rm-Guides')} id="content">
      <div className={bem('-container', 'rm-Container', 'rm-Container_flex', isFullWidth && 'rm-ContainerFull')}>
        {!isLoadingSidebar && <Sidebar activeDoc={document?.slug} categories={sidebar} pathRoot="docs" />}
        <article className={bem('-article', 'rm-Article', 'rm-Guides-SuperHub', isLoading && 'rm-Guides_loading')}>
          {!!isLoading && <Spinner kind="overlay" size="xxl" />}

          <Header
            excerpt={document?.content.excerpt || ''}
            githubLink={document?.href.github_url}
            lang={props.lang}
            slugUrl={document?.slug}
            suggestedEdits={!!enableSuggestedEdits && !!document?.href.github_url}
            superHub={true}
            title={document?.title}
          />

          {!!document && (
            <DocBody
              dehydrated={props.rdmd?.dehydrated}
              docBody={document?.content?.body || ''}
              editUrl={`/update/${routeSection}/${document.slug}`}
              footer={
                <Footer
                  document={document ?? undefined}
                  metricsThumbsEnabled={
                    // TODO: Add this to APIv2 project mapper so we can read
                    // this from ProjectStore instead of ProjectContext.
                    project.metrics.thumbsEnabled
                  }
                  projectSubdomain={projectSubdomain}
                  sidebar={sidebar}
                />
              }
              hasMdxError={hasMdxError}
              hideTOC={hideTOC}
              isAdmin={isAdmin}
              isSuperHub={true}
              mdx
              opts={rdmdOpts}
              showAIDropdown={showAIDropdown}
            />
          )}
          <div className="ModalWrapper" id="tutorialmodal-root" />
        </article>
      </div>
    </main>
  );
}

/**
 * This is a fork of the @routes/Doc/index.jsx component that is used to render the a `/docs/:slug` page
 * when the `project.flags.superHub` is enabled.
 *
 * Because SuperHub uses APIv2 data, and the shape of that data is different from internal
 * Hub api data, we need to fork this component to handle the differences.
 */
export default function SuperHubDoc(props: HubResponseProps<DocRouteProps>) {
  const { document: initialDocument, customBlocks: initialCustomBlocks, sidebar: initialSidebar } = props;
  const { ref } = useScrollTarget();

  return (
    <ConnectSuperHubSidebarToApi initialSidebar={initialSidebar}>
      <RedirectToSidebarFirstPage>
        <RedirectToEmptyParentChild>
          <InitializeSuperHubDocument customBlocks={initialCustomBlocks} document={initialDocument}>
            <ConnectSuperHubDocumentToApi>
              <SectionGate section="docs">
                <ScrollTop smooth target={ref} />
                <ConnectRoute next={Content} props={props} />
              </SectionGate>
            </ConnectSuperHubDocumentToApi>
          </InitializeSuperHubDocument>
        </RedirectToEmptyParentChild>
      </RedirectToSidebarFirstPage>
    </ConnectSuperHubSidebarToApi>
  );
}
